import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useMasters } from '../../hooks/useMasters';
import { ImagePicker } from '../ui/ImagePicker';
import { Field, Select, TextInput } from '../ui/Form';
import { ErrorNotice } from '../ui';

interface Created {
  id: string;
  sku: string;
  name: string;
  metal_id: string;
  default_purity_id?: string | null;
  image_data_uri?: string | null;
}

/**
 * Create a product without leaving the page you are on.
 *
 * A jeweller unpacking a delivery meets new designs all the time, and stopping
 * to go to the Products screen, create one, and come back — losing the half-
 * entered purchase — is the friction that makes a counter dread the software.
 * So the few fields a product needs (a name, a photo, the metal, the purity)
 * live here, in a small panel over the purchase, and the new product drops
 * straight into the line that asked for it.
 *
 * The photo matters most: taken once here, the piece is then found by sight on
 * the sell screen rather than by reading a code.
 */
export function QuickProduct({
  initialName,
  onCreated,
  onCancel,
}: {
  initialName?: string;
  onCreated: (product: Created) => void;
  onCancel: () => void;
}) {
  const masters = useMasters();
  const client = useQueryClient();
  const [name, setName] = useState(initialName ?? '');
  const [sku, setSku] = useState('');
  const [metalId, setMetalId] = useState('');
  const [purityId, setPurityId] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  // A SKU suggested from the name, so nobody invents a code:
  // "22K Gold Bangle" → "22K-GOLD-BANGLE". Editable.
  const suggestedSku = name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/products', {
        sku: sku.trim() || suggestedSku,
        name: name.trim(),
        metal_id: metalId,
        ...(purityId ? { default_purity_id: purityId } : {}),
        ...(photo ? { image_data_uri: photo } : {}),
      }),
    onSuccess: (res) => {
      void client.invalidateQueries({ queryKey: ['products'] });
      onCreated({
        id: res.id,
        sku: sku.trim() || suggestedSku,
        name: name.trim(),
        metal_id: metalId,
        default_purity_id: purityId || null,
        image_data_uri: photo,
      });
    },
    onError: setError,
  });

  const purities = metalId ? masters.puritiesFor(metalId) : [];
  const ready = name.trim().length > 0 && metalId.length > 0;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4" onClick={onCancel}>
      <div
        className="w-full max-w-lg rounded-xl border border-silver-100 bg-surface p-6 shadow-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">New product</h2>
            <p className="mt-0.5 text-sm text-ink-soft">
              The kind of thing you sell — pieces of it are tagged as they arrive.
            </p>
          </div>
          <button className="btn-quiet -mr-2 -mt-1 shrink-0" onClick={onCancel}>
            Cancel
          </button>
        </div>

        <ErrorNotice error={error} />

        <div className="grid grid-cols-[7rem_minmax(0,1fr)] items-start gap-5">
          <div className="pt-5">
            <ImagePicker value={photo} onChange={setPhoto} stacked />
          </div>
          <div className="space-y-4">
            <Field label="Name" required>
              <TextInput value={name} onChange={setName} maxLength={120} placeholder="22K gold bangle" autoFocus />
            </Field>
            <Field label="Code" hint={suggestedSku ? `Suggested: ${suggestedSku}` : 'A short unique code.'}>
              <TextInput
                value={sku}
                onChange={(v) => setSku(v.toUpperCase())}
                maxLength={64}
                placeholder={suggestedSku || 'BNG-22K'}
              />
            </Field>
          </div>
        </div>

        <div className="mt-4 grid items-start gap-4 sm:grid-cols-2">
          <Field label="Metal" required>
            <Select
              value={metalId}
              onChange={(v) => {
                setMetalId(v);
                setPurityId('');
              }}
              options={masters.metalOptions}
              placeholder="Choose…"
            />
          </Field>
          <Field label="Default purity" hint={metalId ? undefined : 'Choose a metal first.'}>
            <Select
              value={purityId}
              onChange={setPurityId}
              options={purities.map((p) => ({ value: p.id, label: p.code }))}
              placeholder={metalId ? 'Choose…' : '—'}
            />
          </Field>
        </div>

        <button
          className="btn-primary mt-6 w-full py-2.5"
          disabled={!ready || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'Creating…' : 'Create and use it'}
        </button>
      </div>
    </div>
  );
}
